import React, { useEffect, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import {
  MARKING_STATUS_STYLES,
  DocumentReviewCorrectionCreate,
  DocumentReviewCorrectionRecord,
  DocumentReviewCorrectionType,
  DocumentReviewDecision,
  MarkingStatus,
  PnidEquipment,
  PnidInstrument,
  PnidNode,
  PnidWizardState,
  RegisterEquipmentContext,
  getEquipmentInstrumentName,
  getInstrumentCategoryName,
} from '../types';
import {
  getInstrumentPrimaryLabel,
  getInstrumentSecondaryLabel,
} from '../utils/instrumentIdentity';
import { pnidApi } from '../api';
import { apiErrorMessage } from '../utils/apiError';
import { isCompoundTag } from '../utils/compoundTag';
import type { CompoundSplitPart } from '../utils/splitEquipment';
import { CompoundSplitTrigger } from './CompoundSplitEditor';
import TagAutocompleteInput from './TagAutocompleteInput';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import {
  describePrimaryDrawing,
  getEquipmentRegisterContext,
  ownedByAnotherDrawing,
  resolvePrimaryMark,
  useRegisterContext,
} from '../hooks/useRegisterContext';
import { BulkApplyOutcome, describeBulkApplyOutcome } from '../utils/bulkApplyOutcome';
import { buildEquipmentEditHref } from '../utils/pnidDeepLink';
import {
  getDirectNodeContextInstruments,
  getEquipmentContextInstruments,
  getInstrumentEffectiveContext,
  getNodeContextInstruments,
} from '../utils/unifiedInstruments';

type SelectTarget =
  | { kind: 'equipment'; id: string }
  | { kind: 'equipmentInstrument'; id: string }
  | { kind: 'node'; id: string }
  | { kind: 'nodeInstrument'; id: string };

/** The fields one equipment row's controls can save, as the reviewer changes them. */
export interface EquipmentReviewEdit {
  equipment_no?: string;
  equipment_type?: string;
  description?: string;
  is_primary_source?: boolean;
}

interface PnidReviewTablesProps {
  pnidState: PnidWizardState | null;
  onSelect: (target: SelectTarget) => void;
  onEdit: (target: SelectTarget) => void;
  onRebox: (target: SelectTarget) => void;
  onDelete: (target: SelectTarget) => void;
  /**
   * Saves one equipment row's edited fields. Rejecting reverts the cell and
   * shows the reason under it, so the table never holds a value the drawing
   * does not. Without it the equipment cells are read-only.
   */
  onEditEquipment?: (equipmentId: string, edit: EquipmentReviewEdit) => Promise<void>;
  /**
   * Splits one equipment's compound tag into the assets it names. Rejecting
   * shows the reason in the split editor and leaves the parts as the reviewer
   * left them. Without it a compound tag's row shows no `Split →` action.
   */
  onSplitEquipment?: (equipmentId: string, parts: CompoundSplitPart[]) => Promise<void>;
  /** Closes this dialog and opens the publish check for the drawing. */
  onOpenPublishCheck?: () => void;
  onMarkReviewed?: (
    target: SelectTarget,
    correction?: DocumentReviewCorrectionCreate
  ) => Promise<void> | void;
  onReorderNodeInstruments?: (
    nodeId: string,
    reorderedInstruments: PnidInstrument[]
  ) => void;
  directNodeInstruments?: PnidInstrument[];
  documentId?: string;
  readOnly?: boolean;
}

const STATUS_ORDER: Record<MarkingStatus, number> = {
  final_and_reviewed: 0,
  final: 1,
  in_progress: 1,
  ai_identified: 2,
  skipped: 3,
};

type RowKind = 'equipment' | 'node' | 'instrument';
type ReviewTargetType = 'equipment' | 'stream' | 'instrument';

interface ReviewTargetContext {
  target: SelectTarget;
  targetType: ReviewTargetType;
  targetRef: string;
  targetPath: string;
  beforeValue: Record<string, any>;
}

interface CorrectionDraft extends ReviewTargetContext {
  decision: DocumentReviewDecision;
  correctionType: DocumentReviewCorrectionType;
  evidenceSourceFileId: string;
  evidencePageNumber: string;
  evidenceRowId: string;
  fieldName: string;
  correctedValue: string;
  note: string;
}

/** One editable field of an equipment row, and the key its draft and error are held under. */
type EquipmentCellField = 'equipment_no' | 'equipment_type' | 'description' | 'is_primary_source';

const equipmentCellKey = (equipmentId: string, field: EquipmentCellField): string =>
  `${equipmentId}:${field}`;

const EQUIPMENT_CELL_CLASS = 'pnid-review-cell';

/** The `Split →` action for a compound tag, styled like the row's other action
 *  buttons but in the compound-tag amber. */
const COMPOUND_SPLIT_TRIGGER_STYLE: React.CSSProperties = {
  padding: '3px 5px',
  border: '1px solid #fde68a',
  background: '#fffbeb',
  color: '#b45309',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
};

/**
 * The equipment cells are controls that read as text until the reviewer reaches
 * them: no border and the row's own status colour at rest, a field boundary on
 * hover and a focus ring while typing.
 */
const EQUIPMENT_CELL_STYLES = `
.${EQUIPMENT_CELL_CLASS} {
  width: 100%;
  box-sizing: border-box;
  padding: 3px 5px;
  margin: -1px 0;
  font: inherit;
  font-size: 11px;
  color: #111827;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
}
.${EQUIPMENT_CELL_CLASS}:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: rgba(255, 255, 255, 0.8);
}
.${EQUIPMENT_CELL_CLASS}:focus {
  outline: none;
  border-color: #2563eb;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}
.${EQUIPMENT_CELL_CLASS}:disabled {
  color: #6b7280;
  cursor: not-allowed;
}
@keyframes pnid-review-row-spin {
  to { transform: rotate(360deg); }
}
`;

/** A small inline spinner shown in place of the status badge while a row's edit is saving. */
function RowSavingSpinner() {
  return (
    <div
      role="status"
      aria-label="Saving"
      style={{
        width: 12,
        height: 12,
        border: '2px solid #93c5fd',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'pnid-review-row-spin 0.8s linear infinite',
      }}
    />
  );
}

function getRowBackground(status: string | null | undefined, kind: RowKind): string {
  if (status === 'final_and_reviewed') return '#ecfdf3'; // light green for reviewed
  if (status === 'skipped') return '#faf5ff';
  if (kind === 'equipment') return '#eff6ff'; // light blue
  if (kind === 'node') return '#fff7ed'; // light orange
  return '#f5f3ff'; // light violet for instruments
}

function getStatusLabel(status?: string | null): string {
  if (status === 'final_and_reviewed') return 'Reviewed';
  if (status === 'final') return 'Final';
  if (status === 'in_progress') return 'In Progress';
  if (status === 'skipped') return 'Skipped';
  return 'Pending';
}

function formatReviewValue(value: any): string {
  if (value == null) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildCorrectionPayload(
  context: ReviewTargetContext,
  decision: DocumentReviewDecision,
  correctionType: DocumentReviewCorrectionType,
  beforeValue: Record<string, any>,
  afterValue: Record<string, any>,
  options?: {
    evidenceSourceFileId?: string | null;
    evidenceRef?: Record<string, any>;
    note?: string | null;
  }
): DocumentReviewCorrectionCreate {
  return {
    target_ref: context.targetRef || null,
    correction_type: correctionType,
    decision,
    evidence_source_file_id: options?.evidenceSourceFileId?.trim() || null,
    evidence_ref: options?.evidenceRef ?? {},
    correction: {
      target: {
        type: context.targetType,
        id: context.target.id,
        path: context.targetPath,
      },
      before: beforeValue,
      after: afterValue,
    },
    note: options?.note?.trim() || null,
  };
}

function equipmentContext(equipment: PnidEquipment): ReviewTargetContext {
  return {
    target: { kind: 'equipment', id: equipment.id },
    targetType: 'equipment',
    targetRef: equipment.equipment_no || equipment.id,
    targetPath: `$.equipments[id=${equipment.id}]`,
    beforeValue: {
      status: equipment.status,
      equipment_no: equipment.equipment_no,
      equipment_type: equipment.equipment_type,
      supporting_refs: equipment.supporting_refs ?? [],
    },
  };
}

function nodeContext(node: PnidNode): ReviewTargetContext {
  return {
    target: { kind: 'node', id: node.id },
    targetType: 'stream',
    targetRef: node.line_no || String(node.node_no) || node.id,
    targetPath: `$.nodes[id=${node.id}]`,
    beforeValue: {
      status: node.status,
      line_no: node.line_no,
      source_ref: node.source_ref,
      dest_ref: node.dest_ref,
      supporting_refs: node.supporting_refs ?? [],
    },
  };
}

function instrumentContext(
  instrument: PnidInstrument,
  kind: 'equipmentInstrument' | 'nodeInstrument'
): ReviewTargetContext {
  return {
    target: { kind, id: instrument.id },
    targetType: 'instrument',
    targetRef: getInstrumentPrimaryLabel(instrument) || instrument.id,
    targetPath: `$.instruments[id=${instrument.id}]`,
    beforeValue: {
      status: instrument.status,
      instrument_type: instrument.instrument_type,
      instrument_text: getInstrumentPrimaryLabel(instrument),
    },
  };
}

function Section({
  title,
  toolbar,
  children,
}: {
  title: string;
  /** What the section offers over its whole table, shown beside the title. */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minHeight: 24,
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
        {toolbar}
      </div>
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflowX: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ padding: 10, fontSize: 12, color: '#6b7280' }}>{text}</div>;
}

function StatusBadge({ status }: { status?: string | null }) {
  const normalizedStatus = (status ?? 'ai_identified') as MarkingStatus;
  const style =
    MARKING_STATUS_STYLES[normalizedStatus] ?? MARKING_STATUS_STYLES.ai_identified;

  return (
    <span
      style={{
        fontSize: 9,
        padding: '2px 5px',
        borderRadius: 4,
        backgroundColor: style.fill,
        color: style.color,
        fontWeight: 500,
        border: `1px ${style.dash ? 'dashed' : 'solid'} ${style.color}`,
      }}
    >
      {getStatusLabel(normalizedStatus)}
    </span>
  );
}

const PnidReviewTables: React.FC<PnidReviewTablesProps> = ({
  pnidState,
  onSelect,
  onEdit,
  onRebox,
  onDelete,
  onEditEquipment,
  onSplitEquipment,
  onOpenPublishCheck,
  onMarkReviewed,
  onReorderNodeInstruments,
  directNodeInstruments: directNodeInstrumentsProp,
  documentId,
  readOnly = false,
}) => {
  const posthog = usePostHog();
  const [draggedItem, setDraggedItem] = useState<{
    nodeId: string;
    instrumentId: string;
  } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reviewingKey, setReviewingKey] = useState<string | null>(null);
  const [correctionDraft, setCorrectionDraft] = useState<CorrectionDraft | null>(null);
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionHistory, setCorrectionHistory] = useState<DocumentReviewCorrectionRecord[]>([]);
  const [correctionHistoryLoading, setCorrectionHistoryLoading] = useState(false);
  const dragRef = useRef<{
    nodeId: string;
    instrumentId: string;
    pointerId: number;
  } | null>(null);

  // Shares the module cache with the equipment edit dialog.
  const registerContext = useRegisterContext(documentId);
  // The same reviewed taxonomy the edit panel's type field offers.
  const equipmentTypes = useEquipmentTypes();

  // What the reviewer has typed into a cell but not yet saved. The ref carries
  // the same map so a blur can read the freshest draft: the tag input rewrites
  // the value inside its own blur, which lands before this one and before any
  // re-render.
  const [equipmentCellDrafts, setEquipmentCellDrafts] = useState<Record<string, string>>({});
  const equipmentCellDraftsRef = useRef<Record<string, string>>({});
  const [equipmentCellErrors, setEquipmentCellErrors] = useState<Record<string, string>>({});
  // Enter commits and then moves focus, which blurs the cell: without this the
  // same edit would be saved twice.
  const savingEquipmentCellsRef = useRef<Set<string>>(new Set());
  /**
   * Equipment ids with a cell or the P&IDs checkbox save in flight. Drives the
   * row-level busy state — every control in that row disables and the status
   * cell shows a spinner until the save resolves (success or revert-on-error).
   */
  const [savingEquipmentRowIds, setSavingEquipmentRowIds] = useState<Set<string>>(new Set());
  // The row's own controls, so Enter can move focus down the column.
  const equipmentCellNodes = useRef<Map<string, HTMLElement>>(new Map());
  /**
   * A primary mark the reviewer just made or released, shown in place of the
   * register's answer until the refetched register agrees. Without it the row
   * would re-lock for as long as the reload takes.
   */
  const [claimedPrimaryByEquipmentId, setClaimedPrimaryByEquipmentId] = useState<
    Record<string, boolean>
  >({});
  /**
   * Equipment that has held the primary mark while this table was open. Only
   * those are ever told they are not primary; the rest stay untouched, the same
   * rule the edit panel's checkbox follows.
   */
  const equipmentsThatCarriedPrimary = useRef<Set<string>>(new Set());
  /**
   * The rows ticked for a bulk apply, and the values the action bar will write
   * to them. An empty value leaves that field of every ticked row as it stands.
   */
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkDescription, setBulkDescription] = useState('');
  /** How far a run has got, so Apply can hold while it walks the rows. */
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkOutcome, setBulkOutcome] = useState<BulkApplyOutcome | null>(null);

  const equipments = pnidState?.equipments ?? [];
  const nodes = pnidState?.nodes ?? [];
  const equipmentInstruments = getEquipmentContextInstruments(pnidState);
  const nodeInstruments = getNodeContextInstruments(pnidState);
  const directNodeInstruments =
    directNodeInstrumentsProp ?? getDirectNodeContextInstruments(pnidState);
  const allInstruments = pnidState?.instruments ?? [];
  const instrumentById = new Map(allInstruments.map((inst) => [inst.id, inst]));

  // Remember every row that holds the mark, whether it arrived with it or was
  // claimed here, so releasing it later sends `false` rather than nothing.
  useEffect(() => {
    (pnidState?.equipments ?? []).forEach((equipment) => {
      const entry = getEquipmentRegisterContext(registerContext.context, equipment.id);
      const thisDrawingIsPrimary = resolvePrimaryMark(entry, {
        liveToggle: null,
        wizardMark: equipment.is_primary_source === true,
      });
      if (thisDrawingIsPrimary) equipmentsThatCarriedPrimary.current.add(equipment.id);
    });
  }, [pnidState, registerContext.context]);

  // The reviewer's claim stands in for the register only until the register
  // says the same thing.
  useEffect(() => {
    const context = registerContext.context;
    if (!context) return;
    setClaimedPrimaryByEquipmentId((current) => {
      const remaining = Object.entries(current).filter(([equipmentId, claimed]) => {
        const entry = getEquipmentRegisterContext(context, equipmentId);
        if (!entry) {
          // With no register record there is nothing to confirm the claim
          // against; the saved equipment itself already carries it.
          return false;
        }
        // Retire the claim once the register states the same answer, read by the
        // rule the rows read, so a stale claim cannot mask a sibling's takeover.
        return resolvePrimaryMark(entry, { liveToggle: null, wizardMark: claimed }) !== claimed;
      });
      if (remaining.length === Object.keys(current).length) return current;
      return Object.fromEntries(remaining);
    });
  }, [registerContext.context]);

  const setEquipmentCellDraft = (key: string, value: string) => {
    equipmentCellDraftsRef.current = { ...equipmentCellDraftsRef.current, [key]: value };
    setEquipmentCellDrafts(equipmentCellDraftsRef.current);
  };

  const clearEquipmentCellDraft = (key: string) => {
    if (!(key in equipmentCellDraftsRef.current)) return;
    const { [key]: _committed, ...rest } = equipmentCellDraftsRef.current;
    equipmentCellDraftsRef.current = rest;
    setEquipmentCellDrafts(rest);
  };

  const setEquipmentCellError = (key: string, message: string | null) => {
    setEquipmentCellErrors((current) => {
      if (message === null) {
        if (!(key in current)) return current;
        const { [key]: _cleared, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: message };
    });
  };

  /** Marks one write as in flight, which puts its row in the busy state. */
  const holdEquipmentRowBusy = (equipmentId: string, cellKey: string) => {
    savingEquipmentCellsRef.current.add(cellKey);
    setSavingEquipmentRowIds((current) => {
      if (current.has(equipmentId)) return current;
      const next = new Set(current);
      next.add(equipmentId);
      return next;
    });
  };

  /**
   * Ends one write. Another cell on the same row may still be saving (rare, but
   * the tag, type, description, P&IDs checkbox and the split can each run
   * independently); only clear the row's busy state once none of its writes
   * remain.
   */
  const releaseEquipmentRowBusy = (equipmentId: string, cellKey: string) => {
    savingEquipmentCellsRef.current.delete(cellKey);
    const rowStillSaving = Array.from(savingEquipmentCellsRef.current).some((key) =>
      key.startsWith(`${equipmentId}:`)
    );
    if (rowStillSaving) return;
    setSavingEquipmentRowIds((current) => {
      if (!current.has(equipmentId)) return current;
      const next = new Set(current);
      next.delete(equipmentId);
      return next;
    });
  };

  /** Saves one row's changed fields, and reports whether the drawing took them. */
  const saveEquipmentEdit = async (
    equipmentId: string,
    cellKey: string,
    edit: EquipmentReviewEdit,
  ): Promise<boolean> => {
    if (!onEditEquipment) return false;
    setEquipmentCellError(cellKey, null);
    holdEquipmentRowBusy(equipmentId, cellKey);
    try {
      await onEditEquipment(equipmentId, edit);
      return true;
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ??
        (error instanceof Error ? error.message : String(error));
      setEquipmentCellError(cellKey, typeof detail === 'string' ? detail : JSON.stringify(detail));
      return false;
    } finally {
      releaseEquipmentRowBusy(equipmentId, cellKey);
    }
  };

  /**
   * Splits one row's compound tag. The row goes busy under its tag cell, which
   * is what the split rewrites, so no other control on the row takes an edit
   * while the parts are being written.
   *
   * A half-applied run renames the row to its first part, which stops the row
   * being compound and takes the `Split →` action — and the editor reporting
   * the failure — off the row. So the reason is written to the tag cell, which
   * outlives the popover, and is also passed back for an editor still on screen.
   */
  const runEquipmentSplit = async (equipmentId: string, parts: CompoundSplitPart[]) => {
    if (!onSplitEquipment) return;
    const cellKey = equipmentCellKey(equipmentId, 'equipment_no');
    setEquipmentCellError(cellKey, null);
    holdEquipmentRowBusy(equipmentId, cellKey);
    try {
      await onSplitEquipment(equipmentId, parts);
    } catch (err) {
      setEquipmentCellError(cellKey, apiErrorMessage(err));
      throw err;
    } finally {
      releaseEquipmentRowBusy(equipmentId, cellKey);
    }
  };

  /**
   * Commits a text cell. Unchanged text is not a save, and a rejected save
   * drops the draft so the cell shows what the drawing actually holds.
   */
  const commitEquipmentCell = async (
    equipment: PnidEquipment,
    field: 'equipment_no' | 'equipment_type' | 'description',
  ) => {
    const cellKey = equipmentCellKey(equipment.id, field);
    if (savingEquipmentCellsRef.current.has(cellKey)) return;
    const draft = equipmentCellDraftsRef.current[cellKey];
    if (draft === undefined) return;
    if (draft === String(equipment[field] ?? '')) {
      clearEquipmentCellDraft(cellKey);
      return;
    }
    await saveEquipmentEdit(equipment.id, cellKey, { [field]: draft });
    clearEquipmentCellDraft(cellKey);
  };

  /**
   * Claims the asset for this drawing, or releases it. A row that never held
   * the mark is left alone rather than told it is not primary.
   */
  const commitEquipmentPrimaryMark = async (equipment: PnidEquipment, claimed: boolean) => {
    const cellKey = equipmentCellKey(equipment.id, 'is_primary_source');
    if (savingEquipmentCellsRef.current.has(cellKey)) return;
    if (!claimed && !equipmentsThatCarriedPrimary.current.has(equipment.id)) return;
    setClaimedPrimaryByEquipmentId((current) => ({ ...current, [equipment.id]: claimed }));
    const saved = await saveEquipmentEdit(equipment.id, cellKey, { is_primary_source: claimed });
    if (saved) {
      if (claimed) equipmentsThatCarriedPrimary.current.add(equipment.id);
      return;
    }
    setClaimedPrimaryByEquipmentId((current) => {
      const { [equipment.id]: _reverted, ...rest } = current;
      return rest;
    });
  };

  const focusEquipmentCell = (equipmentId: string, field: EquipmentCellField) => {
    const node = equipmentCellNodes.current.get(equipmentCellKey(equipmentId, field));
    const control = node?.querySelector('input, select') as HTMLElement | null;
    control?.focus();
  };

  /** What an equipment row shows and what it may do, once the register has answered. */
  const readRowPrimacy = (equipment: PnidEquipment) => {
    const entry = getEquipmentRegisterContext(registerContext.context, equipment.id);
    // A claim made here is the newest statement a reviewer has made and the
    // save has not landed yet, so it outranks the register exactly as the
    // edit panel's live tick does. With no claim the register answers, and it
    // is refetched — a sibling taking the asset over releases the mark here.
    const thisDrawingIsPrimary = resolvePrimaryMark(entry, {
      liveToggle: claimedPrimaryByEquipmentId[equipment.id] ?? null,
      wizardMark: equipment.is_primary_source === true,
    });
    // The drawing that owns the canonical fields is the only one that may edit
    // them — the same rule, from the same helper, the edit panel applies. That
    // is a stricter test than "another drawing owns it": with no drawing
    // primary at all, nobody speaks for the asset, so nobody edits its type or
    // description until a reviewer ticks this drawing.
    return {
      entry,
      thisDrawingIsPrimary,
      ownedElsewhere: ownedByAnotherDrawing(entry, thisDrawingIsPrimary),
      canonicalFieldsEditable: thisDrawingIsPrimary,
    };
  };

  const equipmentCellsEditable = Boolean(onEditEquipment) && !readOnly;

  // The ticked ids name rows of the drawing that was open; another drawing's
  // table has none of them.
  useEffect(() => {
    setSelectedEquipmentIds(new Set());
    setBulkOutcome(null);
  }, [documentId]);

  const selectedEquipments = equipments.filter((equipment) =>
    selectedEquipmentIds.has(equipment.id)
  );
  const bulkApplying = bulkProgress !== null;

  // A changed tick list is a new question, so the last run's report goes with
  // it rather than standing beside rows it says nothing about.
  const toggleEquipmentSelected = (equipmentId: string) => {
    setBulkOutcome(null);
    setSelectedEquipmentIds((current) => {
      const next = new Set(current);
      if (!next.delete(equipmentId)) next.add(equipmentId);
      return next;
    });
  };

  const toggleEveryEquipmentSelected = () => {
    setBulkOutcome(null);
    setSelectedEquipmentIds(
      selectedEquipments.length === equipments.length
        ? new Set()
        : new Set(equipments.map((equipment) => equipment.id))
    );
  };

  /**
   * Writes the action bar's values to every ticked row, one field at a time.
   *
   * A row this drawing does not define is counted as skipped rather than sent:
   * its type and description belong to the drawing that does, which is the same
   * rule that greys those cells. A refused row keeps its reason on its own cell
   * and the run carries on to the next.
   */
  const applyToSelectedEquipments = async () => {
    if (!bulkType && !bulkDescription) return;
    const editable = selectedEquipments.filter(
      (equipment) => readRowPrimacy(equipment).canonicalFieldsEditable
    );
    const outcome: BulkApplyOutcome = {
      applied: 0,
      skipped: selectedEquipments.length - editable.length,
      // An equipment type or description cannot collide two records on one tag.
      duplicates: 0,
      failed: 0,
    };
    setBulkOutcome(null);
    setBulkProgress({ done: 0, total: editable.length });
    for (let index = 0; index < editable.length; index += 1) {
      const equipment = editable[index];
      let rowTaken = true;
      if (bulkType) {
        const cellKey = equipmentCellKey(equipment.id, 'equipment_type');
        if (await saveEquipmentEdit(equipment.id, cellKey, { equipment_type: bulkType })) {
          // A draft the reviewer left in that cell now states something the
          // record does not, so it goes with the write.
          clearEquipmentCellDraft(cellKey);
        } else {
          rowTaken = false;
        }
      }
      if (bulkDescription) {
        const cellKey = equipmentCellKey(equipment.id, 'description');
        if (await saveEquipmentEdit(equipment.id, cellKey, { description: bulkDescription })) {
          clearEquipmentCellDraft(cellKey);
        } else {
          rowTaken = false;
        }
      }
      if (rowTaken) outcome.applied += 1;
      else outcome.failed += 1;
      setBulkProgress({ done: index + 1, total: editable.length });
    }
    setBulkProgress(null);
    setBulkOutcome(outcome);
  };

  /**
   * The equipment table's action bar, shown while rows are ticked and left
   * standing afterwards so its report of the run can be read. A table whose
   * cells are read-only offers no ticking and so has no bar.
   */
  const renderEquipmentBulkBar = () => {
    if (!equipmentCellsEditable) return null;
    if (selectedEquipments.length === 0 && !bulkOutcome) return null;
    const nothingToWrite = !bulkType && !bulkDescription;
    const applyBlocked = bulkApplying || nothingToWrite || selectedEquipments.length === 0;
    const controlStyle: React.CSSProperties = {
      height: 24,
      padding: '0 6px',
      fontSize: 11,
      border: '1px solid #d1d5db',
      borderRadius: 4,
      background: 'white',
      color: '#111827',
    };
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          padding: '5px 8px',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          background: '#eff6ff',
          fontSize: 11,
          color: '#374151',
        }}
      >
        <span style={{ fontWeight: 700, color: '#111827' }}>
          {selectedEquipments.length} selected
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          Type
          <select
            value={bulkType}
            disabled={bulkApplying || equipmentTypes.loading || Boolean(equipmentTypes.error)}
            title={equipmentTypes.error ?? undefined}
            onChange={(event) => setBulkType(event.target.value)}
            style={controlStyle}
          >
            <option value="">Leave unchanged</option>
            {equipmentTypes.types.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          Name
          <input
            value={bulkDescription}
            placeholder="Leave unchanged"
            disabled={bulkApplying}
            onChange={(event) => setBulkDescription(event.target.value)}
            style={{ ...controlStyle, width: 180 }}
          />
        </label>
        <button
          type="button"
          disabled={applyBlocked}
          title={nothingToWrite ? 'Choose a type or type a name to apply' : undefined}
          onClick={() => void applyToSelectedEquipments()}
          style={{
            height: 24,
            padding: '0 12px',
            border: 0,
            borderRadius: 4,
            background: applyBlocked ? '#cbd5e1' : '#2563eb',
            color: 'white',
            fontSize: 11,
            fontWeight: 700,
            cursor: applyBlocked ? 'not-allowed' : 'pointer',
          }}
        >
          Apply
        </button>
        <button
          type="button"
          disabled={bulkApplying}
          onClick={() => {
            setSelectedEquipmentIds(new Set());
            setBulkOutcome(null);
          }}
          style={{
            height: 24,
            padding: '0 10px',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            background: 'white',
            fontSize: 11,
            cursor: bulkApplying ? 'not-allowed' : 'pointer',
          }}
        >
          Clear
        </button>
        {bulkProgress && (
          <span>
            {bulkProgress.done}/{bulkProgress.total}…
          </span>
        )}
        {bulkOutcome && !bulkApplying && <span>{describeBulkApplyOutcome(bulkOutcome)}</span>}
      </div>
    );
  };

  const getAttachedParent = (inst: PnidInstrument): PnidInstrument | null => {
    if (inst.context_type !== 'instrument' || !inst.context_id) return null;
    return instrumentById.get(inst.context_id) ?? null;
  };

  // Flatten instruments into rows, nesting instrument-attached children under
  // their parent and tagging each row with its tree depth + resolved parent.
  const flattenInstrumentRows = (instruments: PnidInstrument[]) => {
    const groupIds = new Set(instruments.map((inst) => inst.id));
    const childrenByParent = new Map<string, PnidInstrument[]>();

    instruments.forEach((inst) => {
      if (
        inst.context_type !== 'instrument' ||
        !inst.context_id ||
        !groupIds.has(inst.context_id)
      ) {
        return;
      }
      const children = childrenByParent.get(inst.context_id) ?? [];
      children.push(inst);
      childrenByParent.set(inst.context_id, children);
    });

    const rows: Array<{
      inst: PnidInstrument;
      depth: number;
      parent: PnidInstrument | null;
    }> = [];
    const rendered = new Set<string>();

    const addWithChildren = (inst: PnidInstrument, depth: number) => {
      if (rendered.has(inst.id)) return;
      rendered.add(inst.id);
      rows.push({ inst, depth, parent: getAttachedParent(inst) });
      (childrenByParent.get(inst.id) ?? []).forEach((child) =>
        addWithChildren(child, depth + 1)
      );
    };

    instruments.forEach((inst) => {
      const hasVisibleParent =
        inst.context_type === 'instrument' &&
        inst.context_id &&
        groupIds.has(inst.context_id);
      if (!hasVisibleParent) addWithChildren(inst, 0);
    });
    instruments.forEach((inst) => addWithChildren(inst, 0));

    return rows;
  };

  const handleExportExcel = async () => {
    if (documentId) {
      posthog?.capture('export_triggered', { diagram_id: documentId });
      try {
        await pnidApi.exportExcel(documentId);
      } catch (error) {
        console.error('Export failed:', error);
      }
    }
  };

  const openCorrectionDraft = (context: ReviewTargetContext) => {
    setCorrectionError(null);
    setCorrectionHistory([]);
    setCorrectionDraft({
      ...context,
      decision: 'corrected',
      correctionType: 'value',
      evidenceSourceFileId: '',
      evidencePageNumber: '',
      evidenceRowId: '',
      fieldName: '',
      correctedValue: '',
      note: '',
    });
    if (documentId && context.targetRef) {
      setCorrectionHistoryLoading(true);
      pnidApi
        .listReviewCorrections(documentId, { targetRef: context.targetRef, limit: 20 })
        .then(setCorrectionHistory)
        .catch((error) => {
          console.warn('Could not load correction history:', error);
          setCorrectionHistory([]);
        })
        .finally(() => setCorrectionHistoryLoading(false));
    }
  };

  const submitCorrectionDraft = async () => {
    if (!documentId || !correctionDraft) return;
    try {
      setCorrectionSaving(true);
      setCorrectionError(null);
      const fieldName = correctionDraft.fieldName.trim();
      const correctedValue = correctionDraft.correctedValue.trim();
      if (correctionDraft.decision === 'corrected' && !fieldName) {
        throw new Error('Select or enter the field being corrected.');
      }
      if (correctionDraft.decision === 'corrected' && !correctedValue) {
        throw new Error('Enter the corrected value.');
      }
      const evidenceRef: Record<string, any> = {};
      const pageNumber = Number.parseInt(correctionDraft.evidencePageNumber.trim(), 10);
      if (!Number.isNaN(pageNumber)) evidenceRef.page_number = pageNumber;
      if (correctionDraft.evidenceRowId.trim()) evidenceRef.row_id = correctionDraft.evidenceRowId.trim();
      const beforeValue = fieldName
        ? { [fieldName]: correctionDraft.beforeValue[fieldName] ?? null }
        : correctionDraft.beforeValue;
      const afterValue = fieldName
        ? { [fieldName]: correctedValue }
        : {};
      const payload = buildCorrectionPayload(
        correctionDraft,
        correctionDraft.decision,
        correctionDraft.correctionType,
        beforeValue,
        afterValue,
        {
          evidenceSourceFileId: correctionDraft.evidenceSourceFileId,
          evidenceRef,
          note: correctionDraft.note,
        }
      );
      await pnidApi.createReviewCorrection(documentId, payload);
      setCorrectionDraft(null);
      setCorrectionHistory([]);
    } catch (error) {
      setCorrectionError(error instanceof Error ? error.message : String(error));
    } finally {
      setCorrectionSaving(false);
    }
  };

  const instrumentsByEquipment = equipments.map((equipment) => ({
    equipment,
    instruments: equipmentInstruments.filter(
      (instrument) =>
        getInstrumentEffectiveContext(instrument, pnidState).context_id === equipment.id
    ),
  }));

  const instrumentsByNode = nodes
    .slice()
    .sort((a, b) => {
      const aOrder = STATUS_ORDER[a.status] ?? 3;
      const bOrder = STATUS_ORDER[b.status] ?? 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.node_no - b.node_no;
    })
    .map((node) => ({
      node,
      instruments: nodeInstruments.filter(
        (instrument) =>
          getInstrumentEffectiveContext(instrument, pnidState).context_id === node.id
      ),
    }));

  const TableActionButtons = ({
    context,
    status,
    busy = false,
    showEdit = true,
    splitButton = null,
  }: {
    context: ReviewTargetContext;
    status?: string | null;
    /** True while this row's own save (a cell commit or the P&IDs checkbox) is in flight. */
    busy?: boolean;
    /**
     * The equipment table drops Edit — its cells are the editor now. Node and
     * instrument rows have no inline cell editing, so they keep it.
     */
    showEdit?: boolean;
    /**
     * The equipment table's `Split →` action for a compound tag, built by the
     * caller so it carries the row's own equipment id, tag and split handler.
     * Node and instrument rows have no compound tag, so they pass none.
     */
    splitButton?: React.ReactNode;
  }) => {
    const target = context.target;
    const actionKey = `${target.kind}:${target.id}`;
    const isReviewed = status === 'final_and_reviewed';
    const isSaving = reviewingKey === actionKey;

    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
        <button
          onClick={() => onSelect(target)}
          disabled={busy}
          style={{
            padding: '3px 5px',
            border: '1px solid #e5e7eb',
            background: 'white',
            borderRadius: 4,
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: 11,
            opacity: busy ? 0.55 : 1,
          }}
        >
          View
        </button>
        {showEdit && (
          <button
            onClick={() => onEdit(target)}
            disabled={readOnly || busy}
            style={{
              padding: '3px 5px',
              border: '1px solid #e5e7eb',
              background: 'white',
              borderRadius: 4,
              cursor: readOnly || busy ? 'not-allowed' : 'pointer',
              fontSize: 11,
              opacity: readOnly || busy ? 0.55 : 1,
            }}
          >
            Edit
          </button>
        )}
        <button
          onClick={() => onRebox(target)}
          disabled={readOnly || busy}
          style={{
            padding: '3px 5px',
            border: '1px solid #e5e7eb',
            background: 'white',
            borderRadius: 4,
            cursor: readOnly || busy ? 'not-allowed' : 'pointer',
            fontSize: 11,
            opacity: readOnly || busy ? 0.55 : 1,
          }}
        >
          {target.kind === 'node' ? 'Redraw' : 'Rebox'}
        </button>
        <button
          onClick={() => onDelete(target)}
          disabled={readOnly || busy}
          style={{
            padding: '3px 5px',
            border: '1px solid #fee2e2',
            background: '#fff1f2',
            color: '#991b1b',
            borderRadius: 4,
            cursor: readOnly || busy ? 'not-allowed' : 'pointer',
            fontSize: 11,
            opacity: readOnly || busy ? 0.55 : 1,
          }}
        >
          Del
        </button>
        {splitButton}
        {onMarkReviewed && (
          <button
            onClick={async () => {
              if (isReviewed || isSaving) return;
              try {
                setReviewingKey(actionKey);
                await onMarkReviewed(target);
              } finally {
                setReviewingKey((current) => (current === actionKey ? null : current));
              }
            }}
            disabled={isReviewed || isSaving || busy}
            style={{
              padding: '3px 5px',
              border: '1px solid #bbf7d0',
              background: isReviewed ? '#dcfce7' : '#f0fdf4',
              color: '#166534',
              borderRadius: 4,
              cursor: isReviewed || isSaving || busy ? 'not-allowed' : 'pointer',
              fontSize: 11,
              fontWeight: 600,
              opacity: busy ? 0.55 : 1,
            }}
          >
            {isSaving ? 'Saving...' : isReviewed ? 'Reviewed' : 'Mark as Reviewed'}
          </button>
        )}
        {documentId && (
          <button
            onClick={() => openCorrectionDraft(context)}
            disabled={busy}
            style={{
              padding: '3px 5px',
              border: '1px solid #fed7aa',
              background: '#fff7ed',
              color: '#9a3412',
              borderRadius: 4,
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 11,
              fontWeight: 600,
              opacity: busy ? 0.55 : 1,
            }}
          >
            Log
          </button>
        )}
      </div>
    );
  };

  const reorder = (items: PnidInstrument[], from: number, to: number) => {
    if (from < 0 || from >= items.length) return items;
    let insertIndex = Math.max(0, Math.min(items.length, to));
    if (insertIndex === from || insertIndex === from + 1) return items;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    if (insertIndex > from) insertIndex -= 1;
    next.splice(insertIndex, 0, moved);
    return next;
  };

  const computeOverIndexFromPointer = (
    nodeId: string,
    clientX: number,
    clientY: number
  ) => {
    const element = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    if (!element) return null;

    const row = element.closest('tr[data-node-id][data-inst-id]') as HTMLTableRowElement | null;
    if (row && row.dataset.nodeId === nodeId) {
      const idx = Number(row.dataset.idx);
      if (!Number.isFinite(idx)) return null;
      const rect = row.getBoundingClientRect();
      const before = clientY < rect.top + rect.height / 2;
      return Math.max(0, idx + (before ? 0 : 1));
    }

    const tbody = element.closest(`tbody[data-node-id="${nodeId}"]`) as
      | HTMLTableSectionElement
      | null;
    if (tbody) {
      return tbody.querySelectorAll('tr[data-inst-id]').length;
    }

    return null;
  };

  const handlePointerDown = (
    event: React.PointerEvent<HTMLElement>,
    nodeId: string,
    instrumentId: string
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    dragRef.current = { nodeId, instrumentId, pointerId: event.pointerId };
    setDraggedItem({ nodeId, instrumentId });
    setDragOverIndex(null);
    document.documentElement.style.userSelect = 'none';
  };

  const endDrag = () => {
    dragRef.current = null;
    setDraggedItem(null);
    setDragOverIndex(null);
    document.documentElement.style.userSelect = '';
  };

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      event.preventDefault();
      setDragOverIndex(
        computeOverIndexFromPointer(drag.nodeId, event.clientX, event.clientY)
      );
    };

    const onUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      event.preventDefault();

      // Only direct node instruments are reorderable; instrument-attached
      // children render in the same flattened list but are pinned under their
      // parents. Map dragOverIndex (a flattened-row index) into a position
      // within the direct list so we don't drop a parent between its children.
      const instruments = directNodeInstruments.filter(
        (instrument) => instrument.context_id === drag.nodeId
      );
      const directIds = new Set(instruments.map((instrument) => instrument.id));
      const visibleNodeInstrumentRows = flattenInstrumentRows(
        nodeInstruments.filter(
          (instrument) =>
            getInstrumentEffectiveContext(instrument, pnidState).context_id === drag.nodeId
        )
      );
      const from = instruments.findIndex((instrument) => instrument.id === drag.instrumentId);
      const to =
        dragOverIndex === null
          ? null
          : visibleNodeInstrumentRows
              .slice(0, dragOverIndex)
              .filter(({ inst }) => directIds.has(inst.id)).length;

      if (from !== -1 && to !== null) {
        const reordered = reorder(instruments, from, to);
        if (reordered !== instruments) {
          onReorderNodeInstruments?.(drag.nodeId, reordered);
        }
      }

      endDrag();
    };

    const onCancel = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      endDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp, { passive: false });
    window.addEventListener('pointercancel', onCancel, { passive: false });

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [
    dragOverIndex,
    directNodeInstruments,
    nodeInstruments,
    onReorderNodeInstruments,
    pnidState,
  ]);

  const renderEquipments = () => {
    if (equipments.length === 0) return <Empty text="No equipments yet." />;

    const currentSourceFileId = registerContext.context?.source_file_id ?? null;
    // The drawing the reviewer is on, by the name it goes by everywhere else.
    const currentDrawingName = pnidState?.pnid_no?.trim() || 'Untitled drawing';

    /** Why a row's type and description are not editable here. */
    const canonicalFieldsNote = (
      entry: RegisterEquipmentContext | null,
      ownedElsewhere: boolean,
    ): string =>
      ownedElsewhere && entry
        ? `${describePrimaryDrawing(entry)} owns this asset's type and description. ` +
          'Tick this drawing under P&IDs to edit them here.'
        : 'Tick this drawing under P&IDs to edit type and description.';

    /**
     * A cell's control, the boundary Enter and blur are caught on, and the
     * reason a save was refused. Called as a function, not mounted as a
     * component, so a keystroke re-render never takes focus off the control.
     */
    const renderEquipmentCell = (
      equipment: PnidEquipment,
      field: EquipmentCellField,
      nextEquipmentId: string | null,
      children: React.ReactNode,
    ) => {
      const cellKey = equipmentCellKey(equipment.id, field);
      const error = equipmentCellErrors[cellKey];
      return (
        <div
          ref={(node) => {
            if (node) equipmentCellNodes.current.set(cellKey, node);
            else equipmentCellNodes.current.delete(cellKey);
          }}
          onBlur={
            field === 'is_primary_source'
              ? undefined
              : () => void commitEquipmentCell(equipment, field)
          }
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.defaultPrevented) return;
            // Enter inside the tag input's dropdown picks a row instead; that
            // handler prevents the default and this one stands down.
            event.preventDefault();
            if (field !== 'is_primary_source') void commitEquipmentCell(equipment, field);
            if (nextEquipmentId) focusEquipmentCell(nextEquipmentId, field);
          }}
        >
          {children}
          {error && (
            <div style={{ fontSize: 9, color: '#b91c1c', marginTop: 2, lineHeight: 1.4 }}>
              {error}
            </div>
          )}
        </div>
      );
    };

    /** Every drawing the asset appears on, as one named checkbox each. */
    const renderPnidsCell = (
      equipment: PnidEquipment,
      entry: RegisterEquipmentContext | null,
      thisDrawingIsPrimary: boolean,
      rowSaving: boolean,
    ) => {
      // A pending or failed read cannot say which drawing is primary, so it says
      // so rather than showing boxes nobody has read yet.
      if (registerContext.loading) return <span style={{ color: '#6b7280' }}>…</span>;
      if (registerContext.error) return <span style={{ color: '#6b7280' }}>unavailable</span>;

      const appearances = entry?.appearances ?? [];
      const boxes = appearances.map((appearance) => {
        const isCurrentDrawing = appearance.source_file_id === currentSourceFileId;
        return {
          sourceFileId: appearance.source_file_id,
          name: isCurrentDrawing
            ? appearance.drawing_no || currentDrawingName
            : appearance.drawing_no || 'Untitled drawing',
          isCurrentDrawing,
          checked: isCurrentDrawing
            ? thisDrawingIsPrimary
            : !thisDrawingIsPrimary && entry?.primary_source_file_id === appearance.source_file_id,
          docId: appearance.doc_id ?? null,
          equipmentId: appearance.equipment_id ?? null,
        };
      });
      // Equipment the register has no record of still marks this drawing, and
      // so does a record that somehow omits it.
      if (!boxes.some((box) => box.isCurrentDrawing)) {
        boxes.push({
          sourceFileId: currentSourceFileId ?? equipment.id,
          name: currentDrawingName,
          isCurrentDrawing: true,
          checked: thisDrawingIsPrimary,
          docId: documentId ?? null,
          equipmentId: equipment.id,
        });
      }

      // The name is a link, not part of the checkbox's label: clicking it opens
      // that drawing rather than moving the mark. Only the box and the ✓ area
      // immediately around it toggle.
      const nameOpensDrawing = Boolean(equipmentCellsEditable && !rowSaving);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {boxes.map((box) => (
            <div
              key={box.sourceFileId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                color: box.isCurrentDrawing ? '#111827' : '#6b7280',
              }}
            >
              <label
                title={
                  box.isCurrentDrawing
                    ? 'Tick to make this drawing own the asset’s canonical fields.'
                    : `${box.name} is another drawing — its mark is changed there.`
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor:
                    box.isCurrentDrawing && equipmentCellsEditable && !rowSaving ? 'pointer' : 'default',
                }}
              >
                <input
                  type="checkbox"
                  checked={box.checked}
                  disabled={!box.isCurrentDrawing || !equipmentCellsEditable || rowSaving}
                  onChange={(event) =>
                    void commitEquipmentPrimaryMark(equipment, event.target.checked)
                  }
                  style={{ margin: 0 }}
                />
              </label>
              {box.docId && nameOpensDrawing ? (
                <a
                  href={buildEquipmentEditHref(box.docId, box.equipmentId)}
                  target="_blank"
                  rel="noreferrer"
                  title={`Open ${box.name} with this equipment’s panel.`}
                  style={{ color: '#0369a1' }}
                >
                  {box.name}
                </a>
              ) : (
                <span>{box.name}</span>
              )}
            </div>
          ))}
        </div>
      );
    };

    /**
     * The tag stays editable on every drawing, whoever holds the mark. A rename
     * re-points this drawing at another asset rather than restating the owner's,
     * and the API resets the marks the old tag carried.
     */
    const renderTagCell = (
      equipment: PnidEquipment,
      entry: RegisterEquipmentContext | null,
      ownedElsewhere: boolean,
      rowSaving: boolean,
    ) => {
      if (!equipmentCellsEditable) return equipment.equipment_no;
      const cellKey = equipmentCellKey(equipment.id, 'equipment_no');
      const stored = ownedElsewhere ? entry?.tag ?? '' : equipment.equipment_no ?? '';
      return (
        <TagAutocompleteInput
          value={equipmentCellDrafts[cellKey] ?? stored}
          onChange={(next) => setEquipmentCellDraft(cellKey, next)}
          docId={documentId}
          kind="equipment"
          disabled={rowSaving}
          unstyled
          className={EQUIPMENT_CELL_CLASS}
        />
      );
    };

    const renderTypeCell = (
      equipment: PnidEquipment,
      entry: RegisterEquipmentContext | null,
      ownedElsewhere: boolean,
      canonicalFieldsEditable: boolean,
      rowSaving: boolean,
    ) => {
      if (!equipmentCellsEditable) return equipment.equipment_type;
      const cellKey = equipmentCellKey(equipment.id, 'equipment_type');
      const stored = ownedElsewhere ? entry?.asset_type ?? '' : equipment.equipment_type ?? '';
      const value = equipmentCellDrafts[cellKey] ?? stored;
      // Only a loaded taxonomy can prove a value is outside it.
      const isUnreviewedType =
        equipmentTypes.loaded &&
        Boolean(value) &&
        !equipmentTypes.types.some((item) => item.key === value);

      if (equipmentTypes.error) {
        // The taxonomy is unreachable and the API skips its check in exactly
        // that case, so the reviewer types the type rather than being trapped.
        return (
          <input
            value={value}
            onChange={(event) => setEquipmentCellDraft(cellKey, event.target.value)}
            disabled={!canonicalFieldsEditable || rowSaving}
            className={EQUIPMENT_CELL_CLASS}
          />
        );
      }

      return (
        <>
          <select
            /* An uneditable cell reports the register's type, which is a label
               and need not be a reviewed key, so it selects the injected option.
               An editable cell shows an unreviewed value as unset, which is what
               makes the reviewer pick a reviewed one. */
            value={isUnreviewedType && canonicalFieldsEditable ? '' : value}
            onChange={(event) => {
              setEquipmentCellDraft(cellKey, event.target.value);
              // A picked option is the whole decision; there is nothing half-typed
              // to wait for.
              void commitEquipmentCell(equipment, 'equipment_type');
            }}
            disabled={!canonicalFieldsEditable || equipmentTypes.loading || rowSaving}
            className={EQUIPMENT_CELL_CLASS}
          >
            <option value="" disabled>
              {equipmentTypes.loading ? 'Loading…' : 'Select a type'}
            </option>
            {/* The register's type need not be in the reviewed list, and an
                uneditable cell must still show what the register holds. */}
            {isUnreviewedType && !canonicalFieldsEditable && <option value={value}>{value}</option>}
            {equipmentTypes.types.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
          {isUnreviewedType && canonicalFieldsEditable && (
            <div style={{ fontSize: 9, color: '#b45309', marginTop: 2, lineHeight: 1.4 }}>
              “{value}” is not in the reviewed list. Choose the reviewed type.
            </div>
          )}
        </>
      );
    };

    const renderDescriptionCell = (
      equipment: PnidEquipment,
      entry: RegisterEquipmentContext | null,
      ownedElsewhere: boolean,
      canonicalFieldsEditable: boolean,
      rowSaving: boolean,
    ) => {
      if (!equipmentCellsEditable) return equipment.description;
      const cellKey = equipmentCellKey(equipment.id, 'description');
      const stored = ownedElsewhere ? entry?.display_name ?? '' : equipment.description ?? '';
      return (
        <input
          value={equipmentCellDrafts[cellKey] ?? stored}
          onChange={(event) => setEquipmentCellDraft(cellKey, event.target.value)}
          disabled={!canonicalFieldsEditable || rowSaving}
          className={EQUIPMENT_CELL_CLASS}
        />
      );
    };

    return (
      <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 11 }}>
        <thead style={{ background: '#f8fafc' }}>
          <tr>
            {equipmentCellsEditable && (
              <th style={{ width: 24, padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                <input
                  type="checkbox"
                  checked={
                    equipments.length > 0 && selectedEquipments.length === equipments.length
                  }
                  ref={(node) => {
                    // Some but not all of the table ticked reads as neither on
                    // nor off.
                    if (node) {
                      node.indeterminate =
                        selectedEquipments.length > 0 &&
                        selectedEquipments.length < equipments.length;
                    }
                  }}
                  disabled={bulkApplying}
                  aria-label="Select every equipment row"
                  onChange={toggleEveryEquipmentSelected}
                  style={{ margin: 0 }}
                />
              </th>
            )}
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
              Equip No
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Type
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Desc
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
              P&amp;IDs (✓ = primary)
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Status
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {equipments.map((equipment, index) => {
            const { entry, thisDrawingIsPrimary, ownedElsewhere, canonicalFieldsEditable } =
              readRowPrimacy(equipment);
            const nextEquipmentId = equipments[index + 1]?.id ?? null;
            const canonicalFieldsTitle = canonicalFieldsEditable
              ? undefined
              : canonicalFieldsNote(entry, ownedElsewhere);
            const rowSaving = savingEquipmentRowIds.has(equipment.id);
            /* The split rewrites the tag, so only the drawing that owns the
               asset's canonical fields may run it — the action itself is
               disabled where another drawing owns it, rather than opening a
               popover the reviewer cannot use.

               A bulk apply walks these same rows, and a split rewrites the tag
               that decides which drawing owns them. The action therefore reads
               the run as busy as well as the row: a row the loop has not
               reached yet is still a row the loop is about to write. */
            const splitButton = !readOnly && onSplitEquipment && isCompoundTag(equipment.equipment_no) ? (
              <CompoundSplitTrigger
                triggerStyle={COMPOUND_SPLIT_TRIGGER_STYLE}
                title={
                  ownedElsewhere
                    ? 'The primary drawing owns this tag. Split it there.'
                    : 'Compound tag — split it into separate assets'
                }
                docId={documentId}
                equipmentId={equipment.id}
                tag={equipment.equipment_no}
                description={equipment.description}
                inheritedType={equipment.equipment_type}
                triggerDisabled={ownedElsewhere}
                busy={rowSaving || bulkApplying}
                onConfirm={(parts) => runEquipmentSplit(equipment.id, parts)}
              />
            ) : null;
            return (
              <tr key={equipment.id} style={{ background: getRowBackground(equipment.status, 'equipment') }}>
                {equipmentCellsEditable && (
                  <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                    <input
                      type="checkbox"
                      checked={selectedEquipmentIds.has(equipment.id)}
                      disabled={bulkApplying}
                      aria-label={`Select ${equipment.equipment_no || 'equipment'}`}
                      onChange={() => toggleEquipmentSelected(equipment.id)}
                      style={{ margin: '4px 0 0' }}
                    />
                  </td>
                )}
                <td
                  style={{ padding: 6, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', minWidth: 90 }}
                >
                  {renderEquipmentCell(
                    equipment,
                    'equipment_no',
                    nextEquipmentId,
                    renderTagCell(equipment, entry, ownedElsewhere, rowSaving),
                  )}
                </td>
                <td
                  title={canonicalFieldsTitle}
                  style={{ padding: 6, borderBottom: '1px solid #f1f5f9', minWidth: 120 }}
                >
                  {renderEquipmentCell(
                    equipment,
                    'equipment_type',
                    nextEquipmentId,
                    renderTypeCell(
                      equipment,
                      entry,
                      ownedElsewhere,
                      canonicalFieldsEditable,
                      rowSaving,
                    ),
                  )}
                </td>
                <td
                  title={canonicalFieldsTitle}
                  style={{ padding: 6, borderBottom: '1px solid #f1f5f9', minWidth: 140 }}
                >
                  {renderEquipmentCell(
                    equipment,
                    'description',
                    nextEquipmentId,
                    renderDescriptionCell(
                      equipment,
                      entry,
                      ownedElsewhere,
                      canonicalFieldsEditable,
                      rowSaving,
                    ),
                  )}
                  {(equipment.supporting_refs?.length ?? 0) > 0 && (
                    <div style={{ fontSize: 9, color: '#0f766e', marginTop: 2 }}>
                      {equipment.supporting_refs?.length} support ref{equipment.supporting_refs?.length === 1 ? '' : 's'}
                    </div>
                  )}
                </td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                  {renderEquipmentCell(
                    equipment,
                    'is_primary_source',
                    null,
                    renderPnidsCell(equipment, entry, thisDrawingIsPrimary, rowSaving),
                  )}
                </td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                  {rowSaving ? <RowSavingSpinner /> : <StatusBadge status={equipment.status} />}
                </td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                  <TableActionButtons
                    context={equipmentContext(equipment)}
                    status={equipment.status}
                    busy={rowSaving}
                    showEdit={false}
                    splitButton={splitButton}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderEquipmentInstruments = () => {
    if (equipments.length === 0) return <Empty text="No equipments yet." />;

    return (
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {instrumentsByEquipment.map(({ equipment, instruments }) => (
          <div key={equipment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div
              style={{
                padding: 6,
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 6,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 11 }}>{equipment.equipment_no}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{instruments.length} items</div>
            </div>
            {instruments.length === 0 ? (
              <div style={{ padding: 6, fontSize: 11, color: '#6b7280' }}>No instruments marked.</div>
            ) : (
              <table
                style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 11 }}
              >
                <thead style={{ background: 'white' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                      Type
                    </th>
                    <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                      Text
                    </th>
                    <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                      Status
                    </th>
                    <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {flattenInstrumentRows(instruments).map(({ inst: instrument, depth, parent }) => (
                    <tr
                      key={instrument.id}
                      style={{ background: getRowBackground(instrument.status, 'instrument') }}
                    >
                      <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ marginLeft: depth * 14 }}>
                          {getEquipmentInstrumentName(instrument.instrument_type)}
                        </div>
                      </td>
                      <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                        <div
                          style={{
                            marginLeft: depth * 14,
                            paddingLeft: depth > 0 ? 8 : 0,
                            borderLeft: depth > 0 ? '2px solid #bfdbfe' : undefined,
                          }}
                        >
                          <div>{getInstrumentPrimaryLabel(instrument)}</div>
                          {parent && (
                            <div style={{ fontSize: 9, color: '#2563eb' }}>
                              Attached to {getInstrumentPrimaryLabel(parent)}
                            </div>
                          )}
                          {getInstrumentSecondaryLabel(instrument) && (
                            <div style={{ fontSize: 9, color: '#9ca3af' }}>
                              {getInstrumentSecondaryLabel(instrument)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                        <StatusBadge status={instrument.status} />
                      </td>
                      <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                        <TableActionButtons
                          context={instrumentContext(instrument, 'equipmentInstrument')}
                          status={instrument.status}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderNodes = () => {
    if (nodes.length === 0) return <Empty text="No nodes yet." />;

    return (
      <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 11 }}>
        <thead style={{ background: '#f8fafc' }}>
          <tr>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              #
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Line
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Src to Dest
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Status
            </th>
            <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {nodes
            .slice()
            .sort((a, b) => {
              const aOrder = STATUS_ORDER[a.status] ?? 3;
              const bOrder = STATUS_ORDER[b.status] ?? 3;
              if (aOrder !== bOrder) return aOrder - bOrder;
              return a.node_no - b.node_no;
            })
            .map((node) => (
              <tr key={node.id} style={{ background: getRowBackground(node.status, 'node') }}>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>{node.node_no}</td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                  {node.line_no}
                  {(node.supporting_refs?.length ?? 0) > 0 && (
                    <div style={{ fontSize: 9, color: '#0f766e', marginTop: 2 }}>
                      {node.supporting_refs?.length} support ref{node.supporting_refs?.length === 1 ? '' : 's'}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    padding: 6,
                    borderBottom: '1px solid #f1f5f9',
                    fontSize: 10,
                    color: '#6b7280',
                  }}
                >
                  {node.source_ref || '-'} to {node.dest_ref || '-'}
                </td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                  <StatusBadge status={node.status} />
                </td>
                <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                  <TableActionButtons context={nodeContext(node)} status={node.status} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    );
  };

  const renderNodeInstruments = () => {
    if (nodes.length === 0) return <Empty text="No nodes yet." />;

    return (
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {instrumentsByNode.map(({ node, instruments }) => {
          const rows = flattenInstrumentRows(instruments);
          return (
            <div key={node.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div
                style={{
                  padding: 6,
                  background: '#f8fafc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 11 }}>Node {node.node_no}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{instruments.length} items</div>
              </div>
              {instruments.length === 0 ? (
                <div style={{ padding: 6, fontSize: 11, color: '#6b7280' }}>No instruments marked.</div>
              ) : (
                <table
                  style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 11 }}
                >
                  <thead style={{ background: 'white' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                        Instrument
                      </th>
                      <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                        Status
                      </th>
                      <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody data-node-id={node.id}>
                    {rows.map(({ inst: instrument, depth, parent }, idx) => {
                      const isDragging = draggedItem?.instrumentId === instrument.id;
                      const isDropLine =
                        draggedItem?.nodeId === node.id && dragOverIndex === idx;
                      const isDirectNodeInstrument =
                        instrument.context_type === 'node' && instrument.context_id === node.id;

                    return (
                      <tr
                        key={instrument.id}
                        data-node-id={node.id}
                        data-inst-id={instrument.id}
                        data-idx={idx}
                        style={{
                          opacity: isDragging ? 0.5 : 1,
                          background: getRowBackground(instrument.status, 'instrument'),
                          cursor: 'default',
                          borderTop: isDropLine ? '2px solid #2563eb' : undefined,
                          transition: 'border-top 0.08s ease',
                        }}
                      >
                        <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                              onPointerDown={
                                isDirectNodeInstrument
                                  ? (event) => handlePointerDown(event, node.id, instrument.id)
                                  : undefined
                              }
                              title={
                                isDirectNodeInstrument
                                  ? 'Drag to reorder'
                                  : 'Instrument-attached children are not reordered with node instruments'
                              }
                              style={{
                                color: '#9ca3af',
                                fontSize: 14,
                                cursor: isDirectNodeInstrument ? 'grab' : 'default',
                                userSelect: 'none',
                                touchAction: 'none',
                                opacity: isDirectNodeInstrument ? 1 : 0.35,
                              }}
                            >
                              ::
                            </span>
                            <div
                              style={{
                                marginLeft: depth * 14,
                                paddingLeft: depth > 0 ? 8 : 0,
                                borderLeft: depth > 0 ? '2px solid #bfdbfe' : undefined,
                              }}
                            >
                              <div>{getInstrumentPrimaryLabel(instrument)}</div>
                              {parent && (
                                <div style={{ fontSize: 9, color: '#2563eb' }}>
                                  Attached to {getInstrumentPrimaryLabel(parent)}
                                </div>
                              )}
                              {getInstrumentSecondaryLabel(instrument) && (
                                <div style={{ fontSize: 9, color: '#9ca3af' }}>
                                  {getInstrumentSecondaryLabel(instrument)}
                                </div>
                              )}
                              {instrument.instrument_type && (
                                <div style={{ fontSize: 9, color: '#9ca3af' }}>
                                  {getInstrumentCategoryName(instrument.instrument_type)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                          <StatusBadge status={instrument.status} />
                        </td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f1f5f9' }}>
                          <TableActionButtons
                            context={instrumentContext(instrument, 'nodeInstrument')}
                            status={instrument.status}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    data-node-id={node.id}
                    style={{
                      borderTop:
                        draggedItem?.nodeId === node.id && dragOverIndex === rows.length
                          ? '2px solid #2563eb'
                          : undefined,
                      height: 18,
                    }}
                  >
                    <td colSpan={3} style={{ padding: 0, borderBottom: '1px solid #f1f5f9' }} />
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: 16,
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
      }}
    >
      <style>{EQUIPMENT_CELL_STYLES}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          P&ID No:{' '}
          <strong style={{ color: '#111827' }}>
            {pnidState?.pnid_no ?? '(not set)'}
          </strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onOpenPublishCheck && (
            <button
              onClick={onOpenPublishCheck}
              disabled={readOnly}
              title={
                readOnly
                  ? 'You need edit access to publish'
                  : 'Close this dialog and check the drawing for publishing'
              }
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#1d4ed8',
                border: '1px solid #1d4ed8',
                borderRadius: 6,
                cursor: readOnly ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
                opacity: readOnly ? 0.55 : 1,
              }}
            >
              Publish
            </button>
          )}
          {documentId && (
            <button
              onClick={handleExportExcel}
              style={{
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Export to Excel
            </button>
          )}
        </div>
      </div>

      {correctionDraft && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            background: 'rgba(15, 23, 42, 0.36)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              width: 'min(720px, 100%)',
              maxHeight: '86vh',
              overflowY: 'auto',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
              padding: 16,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Log review correction</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {correctionDraft.targetType} · {correctionDraft.targetRef}
                </div>
              </div>
              <button
                onClick={() => setCorrectionDraft(null)}
                style={{
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  borderRadius: 6,
                  padding: '4px 8px',
                  cursor: 'pointer',
                  height: 30,
                }}
              >
                Close
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Decision
                <select
                  value={correctionDraft.decision}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, decision: event.target.value as DocumentReviewDecision } : current
                    )
                  }
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                >
                  <option value="corrected">Corrected</option>
                  <option value="rejected">Rejected</option>
                  <option value="flagged">Flagged</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Correction type
                <select
                  value={correctionDraft.correctionType}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, correctionType: event.target.value as DocumentReviewCorrectionType } : current
                    )
                  }
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                >
                  <option value="value">Value</option>
                  <option value="mapping">Mapping</option>
                  <option value="support_mapping">Support mapping</option>
                  <option value="supporting_ref_add">Support ref add</option>
                  <option value="supporting_ref_remove">Support ref remove</option>
                  <option value="supporting_ref_replace">Support ref replace</option>
                  <option value="classification">Classification</option>
                  <option value="extraction">Extraction</option>
                  <option value="missing">Missing</option>
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Evidence source file id
                <input
                  value={correctionDraft.evidenceSourceFileId}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, evidenceSourceFileId: event.target.value } : current
                    )
                  }
                  placeholder="Optional HMB/source file id"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Evidence page
                <input
                  value={correctionDraft.evidencePageNumber}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, evidencePageNumber: event.target.value } : current
                    )
                  }
                  placeholder="Optional page number"
                  inputMode="numeric"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Evidence row id
                <input
                  value={correctionDraft.evidenceRowId}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, evidenceRowId: event.target.value } : current
                    )
                  }
                  placeholder="Optional HMB/support row"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                Field / property
                <input
                  value={correctionDraft.fieldName}
                  onChange={(event) =>
                    setCorrectionDraft((current) =>
                      current ? { ...current, fieldName: event.target.value } : current
                    )
                  }
                  placeholder="Example: line_no, hmb_stream_no, support_ref"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                />
              </label>
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
              Corrected value
              <input
                value={correctionDraft.correctedValue}
                onChange={(event) =>
                  setCorrectionDraft((current) =>
                    current ? { ...current, correctedValue: event.target.value } : current
                  )
                }
                placeholder="Required for corrected decisions"
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
            </label>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Current values</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                {Object.entries(correctionDraft.beforeValue).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr',
                      gap: 8,
                      padding: '6px 8px',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 12,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setCorrectionDraft((current) => current ? { ...current, fieldName: key } : current)}
                      style={{
                        textAlign: 'left',
                        border: 0,
                        background: 'transparent',
                        color: '#2563eb',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {key}
                    </button>
                    <span style={{ color: '#334155', wordBreak: 'break-word' }}>
                      {formatReviewValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Correction history</div>
              {correctionHistoryLoading ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>Loading history...</div>
              ) : correctionHistory.length === 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>No prior corrections for this target.</div>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {correctionHistory.map((entry) => (
                    <div
                      key={entry.id}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, fontSize: 12 }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {entry.decision} · {entry.correction_type}
                      </div>
                      <div style={{ color: '#64748b', marginTop: 2 }}>
                        {entry.created_by} · {new Date(entry.created_at).toLocaleString()}
                      </div>
                      {entry.note && <div style={{ marginTop: 4 }}>{entry.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Note
              <textarea
                value={correctionDraft.note}
                onChange={(event) =>
                  setCorrectionDraft((current) =>
                    current ? { ...current, note: event.target.value } : current
                  )
                }
                rows={3}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
            </label>

            {correctionError && (
              <div style={{ marginTop: 10, color: '#991b1b', fontSize: 12 }}>{correctionError}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button
                onClick={() => setCorrectionDraft(null)}
                disabled={correctionSaving}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  borderRadius: 6,
                  cursor: correctionSaving ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitCorrectionDraft}
                disabled={correctionSaving}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #2563eb',
                  background: '#2563eb',
                  color: 'white',
                  borderRadius: 6,
                  cursor: correctionSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                }}
              >
                {correctionSaving ? 'Saving...' : 'Save correction'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Section title="Equipments" toolbar={renderEquipmentBulkBar()}>
        {renderEquipments()}
      </Section>
      <Section title="Equipment Instrument Details">{renderEquipmentInstruments()}</Section>
      <Section title="Equipment Connection (Nodes)">{renderNodes()}</Section>
      <Section title="Node Instrument Details">{renderNodeInstruments()}</Section>
    </div>
  );
};

export default PnidReviewTables;
